export interface PageMetaInput {
  title: string
  description?: string
  canonical?: string
  robots?: string
  /** Absolute URL for og:image/twitter:image; defaults to the site card. */
  image?: string
  /** og:type; defaults to "website". Pages that set "article" get reset on the next call. */
  ogType?: string
}

const DEFAULT_OG_IMAGE = 'https://returned.com/og-default.jpg'

function ensureMetaTag(selector: string): HTMLMetaElement {
  let tag = document.querySelector(selector) as HTMLMetaElement | null
  if (!tag) {
    tag = document.createElement('meta')
    const match = selector.match(/\[([^=]+)="([^"]+)"\]/)
    if (match) {
      tag.setAttribute(match[1], match[2])
    }
    document.head.appendChild(tag)
  }
  return tag
}

function ensureCanonicalLink(): HTMLLinkElement {
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.rel = 'canonical'
    document.head.appendChild(canonical)
  }
  return canonical
}

export function setPageMeta(input: PageMetaInput): void {
  document.title = input.title

  // Route isolation: canonical always tracks the current route (or the explicit
  // value); robots resets to indexable when a page doesn't say otherwise.
  const canonicalValue = input.canonical ?? `https://returned.com${window.location.pathname}`
  const robotsValue = input.robots ?? 'index, follow'

  if (input.description) {
    const description = ensureMetaTag('meta[name="description"]')
    description.setAttribute('content', input.description)
  }

  ensureMetaTag('meta[name="robots"]').setAttribute('content', robotsValue)
  ensureCanonicalLink().href = canonicalValue

  // Single-writer og/twitter sync: every setPageMeta call fully resets the share
  // card so article-specific values from a previous route can't leak forward.
  document.querySelectorAll('meta[property^="article:"]').forEach((tag) => tag.remove())
  const image = input.image ?? DEFAULT_OG_IMAGE
  const ogPairs: Array<[string, string | undefined]> = [
    ['og:title', input.title],
    ['og:description', input.description],
    ['og:type', input.ogType ?? 'website'],
    ['og:url', canonicalValue],
    ['og:image', image],
  ]
  for (const [property, content] of ogPairs) {
    if (!content) continue
    ensureMetaTag(`meta[property="${property}"]`).setAttribute('content', content)
  }
  const twitterPairs: Array<[string, string | undefined]> = [
    ['twitter:title', input.title],
    ['twitter:description', input.description],
    ['twitter:url', canonicalValue],
    ['twitter:image', image],
  ]
  for (const [name, content] of twitterPairs) {
    if (!content) continue
    ensureMetaTag(`meta[name="${name}"]`).setAttribute('content', content)
  }
}

/**
 * Set (or remove, with data=null) a page-scoped JSON-LD block by id.
 * Callers own cleanup: call with null in their effect teardown.
 */
export function setJsonLd(id: string, data: object | null): void {
  const existing = document.getElementById(id)
  if (existing) existing.remove()
  if (!data) return
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.id = id
  script.textContent = JSON.stringify(data)
  document.head.appendChild(script)
}

